import React from 'react';
import { useQuery } from '@apollo/client';
import { Center, Collapse, Loader, Paper, Stack, Text } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';

import { When } from '../common';
import { GET_PRICE_SYNC_QUEUE_ROW_DETAIL } from '../../apollo/queries/priceSync';
import type { QueueRow, QueueRowDetail } from './priceSync.types';

type PriceSyncRowDetailBodyProps = {
    detail: QueueRowDetail;
};

function PriceSyncRowDetailBody({ detail }: PriceSyncRowDetailBodyProps) {
    return (
        <Stack gap="xs" p="md">
            <When condition={Boolean(detail.lastError)}>
                <Paper p="sm" radius="md" withBorder>
                    <Text size="xs" tt="uppercase" fw={600} c="red" mb={4}>
                        Last error
                    </Text>
                    <Text size="sm" ff="monospace" c="red.8">
                        {detail.lastError}
                    </Text>
                </Paper>
            </When>
            <div>
                <Text size="xs" tt="uppercase" fw={600} c="dimmed" mb={6}>
                    Future prices ({detail.futurePrices.length})
                </Text>
                <When
                    condition={detail.futurePrices.length > 0}
                    fallback={
                        <Text size="sm" c="dimmed">
                            No future-dated prices found for this product.
                        </Text>
                    }
                >
                    <DataTable
                        withTableBorder
                        striped
                        records={detail.futurePrices}
                        columns={[
                            { accessor: 'type', title: 'Type', width: 120 },
                            {
                                accessor: 'price',
                                title: 'Price',
                                render: r =>
                                    `${r.currency ?? ''} ${r.price.toFixed(
                                        2,
                                    )}`.trim(),
                            },
                            {
                                accessor: 'validFrom',
                                title: 'Valid From',
                                render: r =>
                                    r.validFrom
                                        ? dayjs(r.validFrom).format(
                                              'DD MMM YYYY',
                                          )
                                        : '—',
                            },
                            {
                                accessor: 'validTo',
                                title: 'Valid To',
                                render: r =>
                                    r.validTo
                                        ? dayjs(r.validTo).format('DD MMM YYYY')
                                        : '—',
                            },
                        ]}
                    />
                </When>
            </div>
        </Stack>
    );
}

type PriceSyncRowDetailProps = {
    queueId: string;
};

function PriceSyncRowDetail({ queueId }: PriceSyncRowDetailProps) {
    const { data, loading } = useQuery(GET_PRICE_SYNC_QUEUE_ROW_DETAIL, {
        variables: { queueId },
        fetchPolicy: 'cache-and-network',
    });

    const detail: QueueRowDetail | null =
        data?.getPriceSyncQueueRowDetail ?? null;

    return (
        <When
            condition={!loading}
            fallback={
                <Center p="md">
                    <Loader size="sm" />
                </Center>
            }
        >
            <When
                condition={detail !== null}
                fallback={
                    <Text size="sm" c="dimmed" p="md">
                        No detail found.
                    </Text>
                }
            >
                {detail !== null ? (
                    <PriceSyncRowDetailBody detail={detail} />
                ) : null}
            </When>
        </When>
    );
}

export function PriceSyncRowExpansionContent({ record }: { record: QueueRow }) {
    return (
        <Collapse in>
            <PriceSyncRowDetail queueId={record.id} />
        </Collapse>
    );
}
